import {
    USGSSimplePoint,
    attachFeatureHandlers,
    buildSupercluster,
    clusterIconHtml,
    clustersForMap,
    createPointToLayer,
    radiusForZoom,
    toGeoJSON,
    usgsPointToDevice,
} from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/features/utils/USGSMapLayerUtils';
import type { USGSMonitoringStationDevice } from 'features/Integrations/features/ConnectedDevices/types/USGSMONITORINGTYPE';
import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { Popup, useMap } from 'react-leaflet';
import DeviceMapPopup from '../components/popup/DeviceMapPopup';

interface USGSMapLayerProps {
  showAllUSGSDevices: boolean;
  usgsPoints?: USGSSimplePoint[] | null;
  setUseThisDevice?: (device: USGSMonitoringStationDevice) => void;
  setMapCenter?: (coords: [number, number]) => void;
  setMapZoom?: (zoom: number) => void;
  setShowCurrentDevice?: (value: boolean) => void;
  setShowDeviceSettings?: (value: boolean) => void;
  clusteringEnabled?: boolean;
}

/**
 * Performance-optimized USGSMapLayer
 * - Uses a single Leaflet GeoJSON layer with canvas-backed CircleMarkers
 *   instead of thousands of individual React Marker components.
 * - Clicks are handled on the Leaflet layer and routed to a single
 *   React Popup so we only ever render one popup component.
 */
export default function USGSMapLayer({
  showAllUSGSDevices,
  usgsPoints,
  setUseThisDevice,
  setMapCenter,
  setMapZoom,
  setShowCurrentDevice,
  setShowDeviceSettings,
  clusteringEnabled = true,
}: USGSMapLayerProps): JSX.Element | null {
  const map = useMap();
  const geoLayerRef = useRef<L.GeoJSON | null>(null);
  const superIndexRef = useRef<any | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<USGSSimplePoint | null>(null);
  const [popupLatLng, setPopupLatLng] = useState<[number, number] | null>(null);

  useEffect(() => {
    // Clean up if layer exists but should not be shown
    if (!showAllUSGSDevices || !Array.isArray(usgsPoints)) {
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }
      setSelectedPoint(null);
      setPopupLatLng(null);
      return;
    }

    // Remove previous layer before adding a fresh one to keep updates simple
    if (geoLayerRef.current) {
      map.removeLayer(geoLayerRef.current);
      geoLayerRef.current = null;
    }

    // If clustering is disabled, render all points as canvas-backed markers
    if (!clusteringEnabled) {
      const geojson = toGeoJSON(usgsPoints as USGSSimplePoint[]);
      const pointToLayer = createPointToLayer(() => map.getZoom());
      const onEachFeature = attachFeatureHandlers(
        (usgsMapPoint: USGSSimplePoint, latlng: L.LatLng) => {
          setSelectedPoint(usgsMapPoint);
          setPopupLatLng([latlng.lat, latlng.lng]);
        },
        {
          onMouseOver: (layer: L.Layer) =>
            (layer as any).setRadius?.(radiusForZoom(map.getZoom()) + 2),
          onMouseOut: (layer: L.Layer) => (layer as any).setRadius?.(radiusForZoom(map.getZoom())),
        },
      );

      const layer = L.geoJSON(geojson as any, { pointToLayer, onEachFeature });
      geoLayerRef.current = layer;
      geoLayerRef.current.addTo(map);

      // Update all marker radii whenever the user zooms
      const onZoom = () => {
        const radius = radiusForZoom(map.getZoom());
        geoLayerRef.current?.eachLayer((l: any) => l.setRadius?.(radius));
      };
      map.on('zoomend', onZoom);

      return () => {
        map.off('zoomend', onZoom);
        if (geoLayerRef.current) {
          map.removeLayer(geoLayerRef.current);
          geoLayerRef.current = null;
        }
      };
    }

    // Build a Supercluster index, then render cluster features for current map view
    const sc = buildSupercluster(usgsPoints as USGSSimplePoint[]);
    superIndexRef.current = sc;

    const pointFactory = createPointToLayer(() => map.getZoom());

    const updateClusters = () => {
      if (!superIndexRef.current) return;
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      const clusters = clustersForMap(superIndexRef.current, bounds, zoom);

      // Convert clusters returned by supercluster into a geojson FeatureCollection
      const geojsonClusters = { type: 'FeatureCollection' as const, features: clusters };

      // Remove previous cluster layer if present
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }

      const layer = L.geoJSON(geojsonClusters as any, {
        pointToLayer: (feature: any, latlng: L.LatLng) => {
          const props = feature.properties || {};
          // Cluster node
          if (props.cluster) {
            const count = props.point_count || 0;
            const icon = L.divIcon({
              html: clusterIconHtml(count),
              className: 'usgs-cluster-icon',
              iconSize: [40, 40],
              iconAnchor: [20, 20],
            });
            const marker = L.marker(latlng, { icon });
            marker.on('click', () => {
              try {
                const expansion = superIndexRef.current.getClusterExpansionZoom(props.cluster_id);
                map.setView([latlng.lat, latlng.lng], expansion);
              } catch (err) {
                // fallback: zoom in one level
                map.setView(
                  [latlng.lat, latlng.lng],
                  Math.min(map.getMaxZoom(), map.getZoom() + 2),
                );
              }
            });
            return marker;
          }

          // Single point
          const marker = pointFactory(feature, latlng) as any;
          marker.on('click', (e: any) => {
            const usgsPointfromCluster: USGSSimplePoint = feature.properties;
            setSelectedPoint(usgsPointfromCluster);
            setPopupLatLng([e.latlng.lat, e.latlng.lng]);
          });
          marker.on('mouseover', () => marker.setRadius?.(radiusForZoom(map.getZoom()) + 2));
          marker.on('mouseout', () => marker.setRadius?.(radiusForZoom(map.getZoom())));
          return marker;
        },
      });

      geoLayerRef.current = layer;
      geoLayerRef.current.addTo(map);
    };

    // initial render
    updateClusters();

    // update whenever map view changes
    map.on('moveend zoomend', updateClusters);

    return () => {
      map.off('moveend zoomend', updateClusters);
      if (geoLayerRef.current) {
        map.removeLayer(geoLayerRef.current);
        geoLayerRef.current = null;
      }
    };
  }, [map, showAllUSGSDevices, usgsPoints, clusteringEnabled]);

  const closePopup = () => {
    setSelectedPoint(null);
    setPopupLatLng(null);
  };

  // Listen for user-closed popups (clicking the close button) and clear state
  useEffect(() => {
    const handler = (e: any) => {
      const latlng = e?.popup?.getLatLng?.();
      if (!latlng) return;
      if (popupLatLng && latlng.lat === popupLatLng[0] && latlng.lng === popupLatLng[1]) {
        closePopup();
      }
    };

    map.on('popupclose', handler);
    return () => {
      map.off('popupclose', handler);
    };
  }, [map, popupLatLng]);

  if (!showAllUSGSDevices) return null;

  // Render a single Popup controlled by state instead of many Popup children
  return (
    <>
      {popupLatLng && selectedPoint && (
        <Popup position={popupLatLng} className="base-map-popup-leaflet" closeButton={false}>
          <DeviceMapPopup
            device={usgsPointToDevice(selectedPoint)}
            onClose={() => {
              map.closePopup();
              closePopup();
            }}
            onViewDevice={() => {
              // Mirror the behaviour from previous Marker popup
              map.closePopup();
              if (setMapCenter)
                setMapCenter([selectedPoint.coordinates.lat, selectedPoint.coordinates.lng]);
              if (setMapZoom) setMapZoom(Math.max(map.getZoom(), 11));
              closePopup();
            }}
          />
        </Popup>
      )}
    </>
  );
}
